// The page-action cluster in the table header, composed from Buttons the way
// the admin Buttons reference composes a header: the kebab first, the kept
// buttons, then the primary at the end edge. A consumer may instead pass its
// own composed node, which renders unchanged inside the cluster.

import React from 'react'
import PropTypes from 'prop-types'

import { Button, Menu } from '@CommonComponents/buttons'
import { foldPageActions } from './foldActions'

const blocked = action => (action.disabled ? 'true' : undefined)

const ActionButton = ({ action, primary = false }) => (
  <Button
    className={`me-btn ${primary ? 'me-btn-primary' : 'me-btn-secondary'}`}
    href={action.href}
    icon={action.icon}
    aria-disabled={blocked(action)}
    onClick={action.onSelect}
  >
    {action.label}
  </Button>
)

const menuItem = action => (
  <Button
    key={action.key}
    className={`me-menu-item${action.destructive ? ' is-danger' : ''}`}
    href={action.href}
    icon={action.icon ? `${action.icon} me-menu-ico` : undefined}
    aria-disabled={blocked(action)}
    onClick={action.onSelect}
  >
    {action.label}
  </Button>
)

// destructive items sit last, after one separator
const menuItems = overflow => {
  const items = []
  overflow.forEach((action, index) => {
    const previous = overflow[index - 1]
    if (action.destructive && previous && !previous.destructive)
      items.push(
        <div
          key="me-table-danger-sep"
          className="me-menu-sep"
          role="separator"
        />
      )
    items.push(menuItem(action))
  })
  return items
}

export const PageActions = ({ actions = null }) => {
  if (!actions || (Array.isArray(actions) && !actions.length)) return null
  if (!Array.isArray(actions))
    return <div className="me-table-page-actions">{actions}</div>
  const { overflow, visible, primary } = foldPageActions(actions)
  return (
    <div
      className="me-table-page-actions"
      role="group"
      aria-label="Page actions"
    >
      {overflow.length > 0 && (
        <Menu
          label={window.MEButtons.text('strMoreActions')}
          iconOnly
          className="me-btn-icon me-btn-icon-lg"
        >
          {menuItems(overflow)}
        </Menu>
      )}
      {visible.map(action => (
        <ActionButton key={action.key} action={action} />
      ))}
      {primary && <ActionButton action={primary} primary />}
    </div>
  )
}

export const actionShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  href: PropTypes.string,
  icon: PropTypes.string,
  treatment: PropTypes.oneOf(['primary', 'secondary']),
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
})

ActionButton.propTypes = {
  action: actionShape.isRequired,
  primary: PropTypes.bool,
}

PageActions.propTypes = {
  actions: PropTypes.oneOfType([
    PropTypes.arrayOf(actionShape),
    PropTypes.node,
  ]),
}
